/**
 * AICopilotContext
 *
 * Estado global del panel AI Copilot con soporte de streaming.
 * Se monta en AppLayout para estar disponible en toda la app.
 */

import React, { createContext, useCallback, useContext, useMemo, useReducer, useRef } from 'react';
import type {
  AICopilotAction,
  AICopilotContextType,
  AICopilotMessage,
} from '@/types/ai.types';
import { invokeCopilotStream } from '@/lib/ai/copilot';
import { useAuth } from '@smarty/shared';
import { devLog } from '@/lib/utils';

// ─── State ───────────────────────────────────────────────────────────────────

interface CopilotState {
  open: boolean;
  loading: boolean;
  error: string | null;
  contextType: AICopilotContextType;
  contextId: string | null;
  contextLabel: string | null;
  threadId: string | null;
  messages: AICopilotMessage[];
  /** Partial streaming text for the current assistant response */
  streamingText: string | null;
  /** Server connection status: 'thinking' = connected, processing CRM data */
  serverStatus: 'idle' | 'thinking' | 'streaming';
}

const initialState: CopilotState = {
  open: false,
  loading: false,
  error: null,
  contextType: 'general',
  contextId: null,
  contextLabel: null,
  threadId: null,
  messages: [],
  streamingText: null,
  serverStatus: 'idle',
};

// ─── Actions ─────────────────────────────────────────────────────────────────

type CopilotAction =
  | { type: 'OPEN'; contextType?: AICopilotContextType; contextId?: string; contextLabel?: string }
  | { type: 'CLOSE' }
  | { type: 'TOGGLE' }
  | { type: 'SET_CONTEXT'; contextType: AICopilotContextType; contextId: string | null; contextLabel: string | null }
  | { type: 'SEND_START'; userMessage: AICopilotMessage }
  | { type: 'SERVER_STATUS'; status: 'thinking' | 'streaming' }
  | { type: 'STREAM_CHUNK'; token: string }
  | { type: 'STREAM_DONE'; response: string; threadId: string | null }
  | { type: 'SEND_ERROR'; error: string }
  | { type: 'CLEAR_THREAD' }
  | { type: 'CLEAR_ERROR' };

function reducer(state: CopilotState, action: CopilotAction): CopilotState {
  switch (action.type) {
    case 'OPEN':
      return {
        ...state,
        open: true,
        contextType: action.contextType ?? state.contextType,
        contextId: action.contextId ?? state.contextId,
        contextLabel: action.contextLabel ?? state.contextLabel,
        ...(action.contextId && action.contextId !== state.contextId
          ? { threadId: null, messages: [], error: null, streamingText: null }
          : {}),
      };
    case 'CLOSE':
      return { ...state, open: false };
    case 'TOGGLE':
      return { ...state, open: !state.open };
    case 'SET_CONTEXT':
      return {
        ...state,
        contextType: action.contextType,
        contextId: action.contextId,
        contextLabel: action.contextLabel,
        threadId: null,
        messages: [],
        error: null,
        streamingText: null,
      };
    case 'SEND_START':
      return {
        ...state,
        loading: true,
        error: null,
        messages: [...state.messages, action.userMessage],
        streamingText: '',
        serverStatus: 'idle',
      };
    case 'SERVER_STATUS':
      return { ...state, serverStatus: action.status };
    case 'STREAM_CHUNK':
      return {
        ...state,
        streamingText: (state.streamingText ?? '') + action.token,
        serverStatus: 'streaming',
      };
    case 'STREAM_DONE':
      return {
        ...state,
        loading: false,
        streamingText: null,
        serverStatus: 'idle',
        messages: [
          ...state.messages,
          { role: 'assistant', content: action.response, timestamp: new Date().toISOString() },
        ],
        threadId: action.threadId ?? state.threadId,
      };
    case 'SEND_ERROR':
      return { ...state, loading: false, error: action.error, streamingText: null, serverStatus: 'idle' };
    case 'CLEAR_THREAD':
      return { ...state, threadId: null, messages: [], error: null, streamingText: null, serverStatus: 'idle' };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

export interface AICopilotContextValue {
  state: CopilotState;
  open: (opts?: { contextType?: AICopilotContextType; contextId?: string; contextLabel?: string }) => void;
  close: () => void;
  toggle: () => void;
  setContext: (contextType: AICopilotContextType, contextId: string | null, contextLabel: string | null) => void;
  send: (prompt: string, action?: AICopilotAction) => Promise<string | null>;
  quickAction: (action: AICopilotAction) => Promise<string | null>;
  clearThread: () => void;
}

const AICopilotCtx = createContext<AICopilotContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

export function AICopilotProvider({ children }: { children: React.ReactNode }) {
  const { tenantId } = useAuth();
  const [state, dispatch] = useReducer(reducer, initialState);
  const sendingRef = useRef(false);
  // Refs to read current state inside async callbacks without stale closures
  const stateRef = useRef(state);
  stateRef.current = state;
  const tenantIdRef = useRef(tenantId);
  tenantIdRef.current = tenantId;

  const openPanel = useCallback((opts?: { contextType?: AICopilotContextType; contextId?: string; contextLabel?: string }) => {
    dispatch({ type: 'OPEN', ...opts });
  }, []);

  const close = useCallback(() => dispatch({ type: 'CLOSE' }), []);
  const toggle = useCallback(() => dispatch({ type: 'TOGGLE' }), []);

  const setContext = useCallback((contextType: AICopilotContextType, contextId: string | null, contextLabel: string | null) => {
    dispatch({ type: 'SET_CONTEXT', contextType, contextId, contextLabel });
  }, []);

  const clearThread = useCallback(() => dispatch({ type: 'CLEAR_THREAD' }), []);

  const send = useCallback(async (prompt: string, action: AICopilotAction = 'chat'): Promise<string | null> => {
    if (sendingRef.current) return null;
    sendingRef.current = true;

    const userMsg: AICopilotMessage = { role: 'user', content: prompt, timestamp: new Date().toISOString() };
    dispatch({ type: 'SEND_START', userMessage: userMsg });

    let finalResponse: string | null = null;

    try {
      const s = stateRef.current;
      await invokeCopilotStream(
        {
          action,
          context_type: s.contextType !== 'general' ? s.contextType : undefined,
          context_id: s.contextId ?? undefined,
          prompt,
          thread_id: s.threadId ?? undefined,
          tenant_id: tenantIdRef.current ?? undefined,
        },
        (chunk) => {
          if (chunk.error) {
            dispatch({ type: 'SEND_ERROR', error: chunk.error });
            return;
          }
          if (chunk.token) {
            dispatch({ type: 'STREAM_CHUNK', token: chunk.token });
          }
          if (chunk.done) {
            finalResponse = chunk.response ?? null;
            dispatch({ type: 'STREAM_DONE', response: chunk.response ?? '', threadId: chunk.thread_id ?? null });
          }
        },
        (status) => {
          dispatch({ type: 'SERVER_STATUS', status: status === 'thinking' ? 'thinking' : 'streaming' });
        },
      );

      return finalResponse;
    } catch (err) {
      devLog.error('[AI Copilot] send error:', err);
      const msg = err instanceof Error ? err.message : 'Error desconocido';
      dispatch({ type: 'SEND_ERROR', error: msg });
      return null;
    } finally {
      sendingRef.current = false;
    }
  }, []);

  const quickAction = useCallback(async (action: AICopilotAction): Promise<string | null> => {
    const prompts: Record<AICopilotAction, string> = {
      chat: 'Hola, ¿en qué puedes ayudarme?',
      draft_email: 'Genera un email de seguimiento profesional para este contacto.',
      summarize: 'Dame un resumen ejecutivo completo.',
      next_steps: 'Sugiere los próximos pasos concretos que debería tomar.',
      call_prep: 'Prepara un briefing para mi próxima llamada con este contacto.',
      analyze_deal: 'Analiza este deal: probabilidad de cierre, riesgos y recomendaciones.',
      draft_wa: 'Genera un mensaje de WhatsApp de seguimiento.',
    };

    return send(prompts[action], action);
  }, [send]);

  const value = useMemo<AICopilotContextValue>(() => ({
    state,
    open: openPanel,
    close,
    toggle,
    setContext,
    send,
    quickAction,
    clearThread,
  }), [state, openPanel, close, toggle, setContext, send, quickAction, clearThread]);

  return (
    <AICopilotCtx.Provider value={value}>
      {children}
    </AICopilotCtx.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useAICopilot(): AICopilotContextValue {
  const ctx = useContext(AICopilotCtx);
  if (!ctx) throw new Error('useAICopilot must be used within AICopilotProvider');
  return ctx;
}
